import { type FormEvent, useEffect, useState } from 'react';
import type { Pagination } from './types';

type PageItem = number | 'gap';

export function PaginationControls(props: {
  pagination: Pagination;
  disabled?: boolean;
  onPageChange: (page: number) => void;
}): React.JSX.Element | null {
  const [pageInput, setPageInput] = useState(String(props.pagination.page));
  const pagination = props.pagination;

  useEffect(() => {
    setPageInput(String(pagination.page));
  }, [pagination.page]);

  if (pagination.totalItems === 0) {
    return null;
  }

  const firstItem = (pagination.page - 1) * pagination.pageSize + 1;
  const lastItem = Math.min(pagination.page * pagination.pageSize, pagination.totalItems);
  const pageItems = createPageItems(pagination.page, pagination.totalPages);
  return (
    <nav className="pagination" aria-label="List pagination">
      <span className="pagination-summary">{formatNumber(firstItem)}–{formatNumber(lastItem)} of {formatNumber(pagination.totalItems)}</span>
      {pagination.totalPages > 1 && <>
        <div className="pagination-pages">
          <PageButton page={pagination.page - 1} label="Previous" disabled={props.disabled === true || pagination.page === 1} onSelect={props.onPageChange} />
          {pageItems.map(renderPageItem)}
          <PageButton page={pagination.page + 1} label="Next" disabled={props.disabled === true || pagination.page === pagination.totalPages} onSelect={props.onPageChange} />
        </div>
        <form className="pagination-jump" onSubmit={submitPage}>
          <label>Page <input type="number" min="1" max={pagination.totalPages} value={pageInput} onChange={changePageInput} disabled={props.disabled} /></label>
          <button className="secondary-button" type="submit" disabled={props.disabled}>Go</button>
        </form>
      </>}
    </nav>
  );

  function renderPageItem(item: PageItem, index: number): React.JSX.Element {
    if (item === 'gap') {
      return <span className="pagination-gap" aria-hidden="true" key={`gap-${index}`}>…</span>;
    }
    return (
      <PageButton
        key={item}
        page={item}
        label={String(item)}
        active={item === pagination.page}
        disabled={props.disabled === true}
        onSelect={props.onPageChange}
      />
    );
  }

  function changePageInput(event: React.ChangeEvent<HTMLInputElement>): void {
    setPageInput(event.target.value);
  }

  function submitPage(event: FormEvent): void {
    event.preventDefault();
    const requestedPage = Number(pageInput);
    if (!Number.isInteger(requestedPage)) {
      setPageInput(String(pagination.page));
      return;
    }
    props.onPageChange(Math.max(1, Math.min(requestedPage, pagination.totalPages)));
  }
}

function PageButton(props: {
  page: number;
  label: string;
  active?: boolean;
  disabled: boolean;
  onSelect: (page: number) => void;
}): React.JSX.Element {
  return (
    <button
      className={props.active === true ? 'pagination-button active' : 'pagination-button'}
      type="button"
      aria-current={props.active === true ? 'page' : undefined}
      aria-label={props.label === String(props.page) ? `Page ${props.label}` : props.label}
      disabled={props.disabled}
      onClick={selectPage}
    >
      {props.label}
    </button>
  );

  function selectPage(): void {
    props.onSelect(props.page);
  }
}

function createPageItems(currentPage: number, totalPages: number): PageItem[] {
  const pages = new Set<number>([1, totalPages]);
  for (let page = currentPage - 2; page <= currentPage + 2; page += 1) {
    if (page >= 1 && page <= totalPages) pages.add(page);
  }
  const orderedPages = [...pages].sort(sortNumbers);
  const items: PageItem[] = [];
  for (const page of orderedPages) {
    const previous = items.at(-1);
    if (typeof previous === 'number' && page - previous > 1) items.push('gap');
    items.push(page);
  }
  return items;
}

function sortNumbers(left: number, right: number): number {
  return left - right;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value);
}
